import React, { useEffect, useState, lazy } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card, CardHeader, CardContent } from '../components/Card';
import { Badge } from '../components/Badge';
import { SystemModulesInteractive } from '../components/SystemModulesInteractive';
import { ArrowRight, Check, Clock, Users, Zap, BarChart, Shield, Target, Brain, Repeat } from 'lucide-react';
interface LandingPageProps {
  onNavigate?: (path: string) => void;
}
const backgroundImages = ['https://cdn.magicpatterns.com/uploads/4BVkDo8bz3xGY1aWzcf7pe/unnamed_(6).jpg', 'https://cdn.magicpatterns.com/uploads/eF2BjYrMMHCC1C5TEQuAPa/unnamed_(3).jpg', 'https://cdn.magicpatterns.com/uploads/9qAxTj9M7ApGgRmHmuaVUt/unnamed_(4).jpg', 'https://cdn.magicpatterns.com/uploads/jPnnHfsN658sfmtgSgz29n/unnamed_(7).jpg'];
export function LandingPage({
  onNavigate
}: LandingPageProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % backgroundImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] font-sans selection:bg-salon-red/20 selection:text-[#1f2933]" data-id="element-1277">
      <Navbar onNavigate={onNavigate} data-id="element-1278" />

      {/* HERO SECTION with Background Slider Underlay */}
      <section className="relative border-b border-dashed border-[#9ca3af] py-24 sm:py-32 overflow-hidden" data-id="element-1279">
        {/* Background Image Slider - More visible now */}
        <div className="absolute inset-0 z-0" data-id="element-1280">
          {backgroundImages.map((img, idx) => <div key={idx} className={`absolute inset-0 transition-opacity duration-1000 ${idx === currentImageIndex ? 'opacity-100' : 'opacity-0'}`} data-id="element-1281">
              <img src={img} alt="" loading="lazy" className="w-full h-full object-cover grayscale opacity-30" data-id="element-1282" />
            </div>)}
          {/* Lighter gradient overlay for better visibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#f5f5f7]/60 via-[#f5f5f7]/70 to-[#f5f5f7]" data-id="element-1283" />
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 z-10 bg-[linear-gradient(to_right,#9ca3af20_1px,transparent_1px),linear-gradient(to_bottom,#9ca3af20_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" data-id="element-1284" />

        <div className="relative z-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-1285">
          <Badge variant="secondary" className="mb-8 mx-auto font-mono text-xs tracking-wider" data-id="element-1286">
            SYSTEM_V1.0 // ONLINE
          </Badge>

          <h1 className="mx-auto max-w-4xl text-5xl font-bold tracking-tight sm:text-7xl mb-2 text-[#1f2933]" data-id="element-1287">
            THE AUTHORITY ENGINE
          </h1>

          <p className="font-mono text-sm text-[#9ca3af] mb-6" data-id="element-1288">
            An initiative by The Networking Salon Ltd.
          </p>

          <p className="mx-auto max-w-2xl text-lg font-mono text-[#1f2933]/80 mb-10 leading-relaxed" data-id="element-1289">
            Turn consistent content into authority, visibility, and income.
            <br data-id="element-1290" />
            <span className="text-salon-red font-bold" data-id="element-1291">
              Build authority once. Let it compound.
            </span>
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1292">
            <Button size="large" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-1294" />} onClick={() => onNavigate && onNavigate('/initiate-access#payment')} className="bg-salon-red hover:bg-salon-red/90 border-salon-red" data-id="element-1293">
              INITIATE_ACCESS
            </Button>
            <Button variant="secondary" size="large" onClick={() => onNavigate && onNavigate('/drilling-pot')} className="border-salon-teal text-salon-teal hover:bg-salon-teal hover:text-white" data-id="element-1295">
              ONE_ON_ONE_ACCESS
            </Button>
            <Button variant="secondary" size="large" onClick={() => onNavigate && onNavigate('/blueprint')} data-id="element-1296">
              VIEW_BLUEPRINT
            </Button>
          </div>

          <div className="mt-8" data-id="element-1297">
            <button onClick={() => onNavigate && onNavigate('/drilling-pot')} className="text-xs font-mono text-[#9ca3af] hover:text-salon-red underline underline-offset-4 transition-colors" data-id="element-1298">
              Looking for private one-on-one apprenticeship? Explore The
              Drilling Pot →
            </button>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section id="pain-points" className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-1299">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-1300">
          <div className="mb-16" data-id="element-1301">
            <h2 className="font-mono text-xs font-bold text-salon-red mb-2" data-id="element-1302">
              01_DIAGNOSTICS
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933]" data-id="element-1303">
              System Failures Detected
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1304">
            {[{
            icon: Clock,
            title: 'Bandwidth Critical',
            desc: 'No time to create consistent, high-quality output while running a business.'
          }, {
            icon: Users,
            title: 'Isolation',
            desc: 'Building alone leads to echo chambers and lack of objective feedback.'
          }, {
            icon: Repeat,
            title: 'Inconsistency',
            desc: 'The cycle of start-stop-start destroys momentum and authority compounding.'
          }].map((item, i) => <Card key={i} variant="outlined" className="bg-transparent border-dashed border-[#9ca3af] hover:border-salon-red transition-colors" data-id="element-1305">
                <CardHeader title={item.title} className="font-bold" data-id="element-1306" />
                <CardContent data-id="element-1307">
                  <item.icon className="w-8 h-8 text-salon-red mb-4" data-id="element-1308" />
                  <p className="font-mono text-sm text-[#1f2933]/80" data-id="element-1309">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* ORIGIN STORY */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#1f2933] text-[#f5f5f7]" data-id="element-1310">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8" data-id="element-1311">
          <h2 className="font-mono text-xs font-bold text-salon-red mb-8" data-id="element-1312">
            02_ORIGIN_LOG
          </h2>

          <div className="space-y-8 text-lg leading-relaxed" data-id="element-1313">
            <p className="font-mono border-l-2 border-salon-red pl-6 text-[#9ca3af]" data-id="element-1314">
              "Motivation is not the issue. The system is broken."
            </p>

            <p data-id="element-1315">
              Most professionals fail at content not because they lack ideas,
              but because they lack infrastructure. They treat authority
              building as a creative project rather than an operational process.
            </p>

            <p data-id="element-1316">
              The Authority Engine was built to replace chaos with control. It
              is not a course. It is an operating system for your professional
              reputation.
            </p>

            <p className="text-salon-yellow font-bold" data-id="element-1317">
              You'll learn to create your own content framework and build your
              personal content machine that generates ideas and polished content
              on autopilot.
            </p>
          </div>
        </div>
      </section>

      {/* CORE BENEFITS */}
      <section id="benefits" className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-1318">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-1319">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-1320">
            <div data-id="element-1321">
              <h2 className="font-mono text-xs font-bold text-salon-teal mb-2" data-id="element-1322">
                03_OUTCOMES
              </h2>
              <h3 className="text-4xl font-bold text-[#1f2933] mb-6" data-id="element-1323">
                Engine Output
              </h3>
              <p className="font-mono text-sm text-[#1f2933]/70 mb-8" data-id="element-1324">
                Expected results from system implementation.
              </p>

              <ul className="space-y-4" data-id="element-1325">
                {['Consistent Authority Compounding', 'Accelerated Visibility & Deal Flow', 'Built-in Accountability Loops', 'AI-Powered Operational Efficiency', 'Custom Content Framework & Machine', 'Peer Network & Feedback'].map((benefit, i) => <li key={i} className="flex items-center gap-3 border-b border-dashed border-[#9ca3af]/30 pb-2" data-id="element-1326">
                    <Check className="w-4 h-4 text-salon-red" data-id="element-1327" />
                    <span className="text-[#1f2933] font-medium" data-id="element-1328">
                      {benefit}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-1329">
              <div className="aspect-square border-2 border-dashed border-[#1f2933] flex items-center justify-center p-4 hover:bg-salon-red hover:text-white transition-colors" data-id="element-1330">
                <div className="text-center" data-id="element-1331">
                  <BarChart className="w-8 h-8 mx-auto mb-2" data-id="element-1332" />
                  <span className="font-mono text-xs font-bold" data-id="element-1333">METRICS</span>
                </div>
              </div>
              <div className="aspect-square bg-salon-teal text-white flex items-center justify-center p-4" data-id="element-1334">
                <div className="text-center" data-id="element-1335">
                  <Target className="w-8 h-8 mx-auto mb-2" data-id="element-1336" />
                  <span className="font-mono text-xs font-bold" data-id="element-1337">FOCUS</span>
                </div>
              </div>
              <div className="aspect-square bg-salon-yellow/20 flex items-center justify-center p-4" data-id="element-1338">
                <div className="text-center" data-id="element-1339">
                  <Shield className="w-8 h-8 mx-auto mb-2" data-id="element-1340" />
                  <span className="font-mono text-xs font-bold" data-id="element-1341">TRUST</span>
                </div>
              </div>
              <div className="aspect-square border-2 border-salon-red flex items-center justify-center p-4 hover:bg-salon-red hover:text-white transition-colors" data-id="element-1342">
                <div className="text-center" data-id="element-1343">
                  <Zap className="w-8 h-8 mx-auto mb-2" data-id="element-1344" />
                  <span className="font-mono text-xs font-bold" data-id="element-1345">SPEED</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE SYSTEM - Interactive */}
      <SystemModulesInteractive data-id="element-1346" />

      {/* AI LEVERAGE */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-1347">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-1348">
          <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-1349">
            <div className="w-full md:w-1/2" data-id="element-1350">
              <h2 className="font-mono text-xs font-bold text-salon-yellow mb-2" data-id="element-1351">
                05_LEVERAGE
              </h2>
              <h3 className="text-3xl font-bold text-[#1f2933] mb-6" data-id="element-1352">
                AI as Infrastructure
              </h3>
              <p className="font-mono text-sm text-[#1f2933]/80 mb-4" data-id="element-1353">
                We don't use AI to replace your voice. We use it to amplify your
                thinking.
              </p>
              <p className="font-bold text-salon-red mb-6" data-id="element-1354">
                Build your own content machine: Idea → Expansion → Refinement →
                Distribution.
              </p>
              <Button variant="secondary" onClick={() => onNavigate && onNavigate('/ai-tools')} data-id="element-1355">
                EXPLORE_AI_TOOLS
              </Button>
            </div>

            <div className="w-full md:w-1/2" data-id="element-1356">
              <div className="border-2 border-dashed border-[#9ca3af] p-8 relative bg-white" data-id="element-1357">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#f5f5f7] px-2" data-id="element-1358">
                  <Brain className="w-6 h-6 text-salon-teal" data-id="element-1359" />
                </div>

                <div className="flex flex-col gap-4" data-id="element-1360">
                  <div className="border border-[#1f2933] p-3 text-center font-mono text-xs" data-id="element-1361">
                    RAW_INPUT
                  </div>
                  <div className="h-4 border-l border-dashed border-[#1f2933] mx-auto" data-id="element-1362"></div>
                  <div className="bg-salon-teal text-white p-3 text-center font-mono text-xs" data-id="element-1363">
                    AI_PROCESSING
                  </div>
                  <div className="h-4 border-l border-dashed border-[#1f2933] mx-auto" data-id="element-1364"></div>
                  <div className="border border-[#1f2933] p-3 text-center font-mono text-xs" data-id="element-1365">
                    HUMAN_REFINEMENT
                  </div>
                  <div className="h-4 border-l border-dashed border-[#1f2933] mx-auto" data-id="element-1366"></div>
                  <div className="border-2 border-salon-red p-3 text-center font-bold text-sm" data-id="element-1367">
                    AUTHORITY_ASSET
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OBJECTION HANDLING */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#e5e5e7]" data-id="element-1368">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-1369">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1370">
            <Card className="bg-[#f5f5f7] border-salon-red" data-id="element-1371">
              <CardHeader title="I don't have time." data-id="element-1372" />
              <CardContent data-id="element-1373">
                <p className="font-mono text-sm text-[#1f2933]/70" data-id="element-1374">
                  That is exactly why you need a system. Chaos takes time.
                  Structure buys it back. Your content machine will reduce
                  creation time by 80%.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-[#f5f5f7] border-salon-red" data-id="element-1375">
              <CardHeader title="I've tried before." data-id="element-1376" />
              <CardContent data-id="element-1377">
                <p className="font-mono text-sm text-[#1f2933]/70" data-id="element-1378">
                  You relied on motivation. We rely on accountability loops and
                  operational workflows. This is infrastructure, not
                  inspiration.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* QUIET PROMISE */}
      <section className="py-32 text-center" data-id="element-1379">
        <div className="mx-auto max-w-4xl px-4" data-id="element-1380">
          <h2 className="text-4xl md:text-6xl font-bold text-[#1f2933] mb-8 tracking-tight" data-id="element-1381">
            This isn't about posting.
            <br data-id="element-1382" />
            <span className="text-salon-red" data-id="element-1383">This is about leverage.</span>
          </h2>

          <div className="flex justify-center gap-8 font-mono text-xs text-[#1f2933] mb-12" data-id="element-1384">
            <span data-id="element-1385">CLARITY</span>
            <span data-id="element-1386">CONFIDENCE</span>
            <span data-id="element-1387">INTENT</span>
          </div>

          <Button size="large" className="min-w-[200px] bg-salon-red hover:bg-salon-red/90 border-salon-red" onClick={() => onNavigate && onNavigate('/initiate-access#payment')} data-id="element-1388">
            JOIN_THE_ENGINE
          </Button>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-1389" />
    </div>;
}